/**
 * Question World Module - QuestionFeedback Component
 * Feedback/comment system for questions
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CommentOutlined, CloseOutlined } from '@ant-design/icons';
import { message, Spin } from 'antd';
import { instance } from '../../helpers/api';
import '../../styles/QuestionFeedback.css';

function QuestionFeedback({ question = {} }) {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [hasNewReply, setHasNewReply] = useState(false);

    const [feedbackDoc, setFeedbackDoc] = useState(null);
    const [feedbackText, setFeedbackText] = useState('');
    const [editingFeedback, setEditingFeedback] = useState(false);

    const [replies, setReplies] = useState([]);
    const [replyText, setReplyText] = useState('');
    const [editingReplyIndex, setEditingReplyIndex] = useState(null);
    const [editingReplyId, setEditingReplyId] = useState(null);
    const [editingReplyText, setEditingReplyText] = useState('');
    const [confirmDeleteFeedback, setConfirmDeleteFeedback] = useState(false);
    const [confirmDeleteReplyIndex, setConfirmDeleteReplyIndex] = useState(null);

    const student = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('student') || '{}');
        } catch (error) {
            return {};
        }
    }, []);

    const studentId = student?.id || student?._id;

    const ensureContext = useCallback(() => {
        if (!studentId) {
            message.error('Please login again to give feedback.');
            return false;
        }
        if (!question?._id) {
            message.error('Question context missing.');
            return false;
        }
        return true;
    }, [question?._id, studentId]);

    const closeModal = () => {
        setIsOpen(false);
        setEditingFeedback(false);
        setEditingReplyIndex(null);
        setEditingReplyId(null);
        setEditingReplyText('');
        setConfirmDeleteFeedback(false);
        setConfirmDeleteReplyIndex(null);
    };

    const applyFromDocument = (doc) => {
        if (!doc) {
            setFeedbackDoc(null);
            setFeedbackText('');
            setReplies([]);
            return;
        }
        setFeedbackDoc(doc);
        setFeedbackText('');
        setReplies(doc.replies || []);
        const hasAuthorReply = (doc.replies || []).some((reply) => reply.from === 'author');
        if (hasAuthorReply) {
            setHasNewReply(true);
        }
    };

    const loadFeedbackFromServer = useCallback(async () => {
        if (!ensureContext()) return;
        setIsLoading(true);
        try {
            const response = await instance.get('/student-question-feeback', {
                params: {
                    student_id: studentId,
                    question_id: question._id
                }
            });
            const list = response?.data?.data || [];
            const doc = list.length > 0 ? list[0] : null;
            applyFromDocument(doc);
            setHasLoaded(true);
        } catch (error) {
            const title = error?.response?.data?.errors?.title || 'Unable to load feedback.';
            message.error(title);
        } finally {
            setIsLoading(false);
        }
    }, [ensureContext, question._id, studentId]);

    const openOrToggle = async () => {
        if (!ensureContext()) return;
        if (!hasLoaded) {
            setIsOpen(true);
            await loadFeedbackFromServer();
            setHasNewReply(false);
            return;
        }
        setIsOpen((prev) => !prev);
        if (!isOpen) {
            setHasNewReply(false);
        }
    };

    const hasAuthorReply = useMemo(
        () => replies.some((reply) => reply.from === 'author'),
        [replies]
    );

    const studentCommentCount = useMemo(() => {
        let count = 0;
        if (feedbackDoc && feedbackDoc.student_id === studentId) {
            count += 1;
        }
        const studentReplies = replies.filter(
            (reply) => reply.from === 'student' && reply.author_id === studentId
        );
        count += studentReplies.length;
        return count;
    }, [feedbackDoc, replies, studentId]);

    const MAX_STUDENT_COMMENTS = 2;
    const canAddNewComment = studentCommentCount < MAX_STUDENT_COMMENTS;

    const handleCreateFeedback = async () => {
        if (!ensureContext()) return;
        if (!canAddNewComment) {
            message.error('You have reached the maximum limit of 2 comments.');
            return;
        }
        const text = feedbackText.trim();
        if (!text) {
            message.error('Please write something before sending.');
            return;
        }
        setIsLoading(true);
        try {
            const response = await instance.post('/student-question-feeback', {
                student_id: studentId,
                question_id: question._id,
                feedback: text
            });
            const doc = response?.data?.data;
            applyFromDocument(doc);
            setHasLoaded(true);
        } catch (error) {
            const title = error?.response?.data?.errors?.title || 'Unable to send feedback.';
            message.error(title);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStartEditFeedback = () => {
        if (!feedbackDoc) return;
        setEditingFeedback(true);
        setFeedbackText(feedbackDoc.feedback || '');
        setConfirmDeleteFeedback(false);
    };

    const handleCancelEditFeedback = () => {
        setEditingFeedback(false);
        setFeedbackText('');
        setConfirmDeleteFeedback(false);
    };

    const handleSaveFeedback = async () => {
        if (!feedbackDoc) return;
        const text = feedbackText.trim();
        if (!text) {
            message.error('Feedback cannot be empty.');
            return;
        }
        setIsLoading(true);
        try {
            const response = await instance.patch(
                `/student-question-feeback/${feedbackDoc._id}`,
                { feedback: text }
            );
            const doc = response?.data?.data;
            applyFromDocument(doc);
            setEditingFeedback(false);
        } catch (error) {
            const title = error?.response?.data?.errors?.title || 'Unable to update feedback.';
            message.error(title);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteFeedback = async () => {
        if (!feedbackDoc) return;
        if (!confirmDeleteFeedback) {
            setConfirmDeleteFeedback(true);
            return;
        }
        setIsLoading(true);
        try {
            await instance.delete(`/student-question-feeback/${feedbackDoc._id}`);
            applyFromDocument(null);
            setHasLoaded(true);
        } catch (error) {
            const title = error?.response?.data?.errors?.title || 'Unable to delete feedback.';
            message.error(title);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmitReply = async () => {
        if (!feedbackDoc) return;
        if (!canAddNewComment) {
            message.error('You have reached the maximum limit of 2 comments.');
            return;
        }
        const text = replyText.trim();
        if (!text) {
            message.error('Reply cannot be empty.');
            return;
        }
        setIsLoading(true);
        try {
            const response = await instance.post(
                `/student-question-feeback/${feedbackDoc._id}/replies`,
                {
                    from: 'student',
                    text,
                    author_id: studentId
                }
            );
            const doc = response?.data?.data;
            applyFromDocument(doc);
            setReplyText('');
        } catch (error) {
            const title = error?.response?.data?.errors?.title || 'Unable to send reply.';
            message.error(title);
        } finally {
            setIsLoading(false);
        }
    };

    const handleStartEditReply = (replyIndex, reply) => {
        if (
            replyIndex === null ||
            replyIndex === undefined ||
            !reply ||
            reply.from !== 'student' ||
            reply.author_id !== studentId
        )
            return;
        setEditingReplyIndex(replyIndex);
        setEditingReplyId(reply._id || null);
        setEditingReplyText(reply.text || '');
        setConfirmDeleteReplyIndex(null);
    };

    const handleCancelEditReply = () => {
        setEditingReplyIndex(null);
        setEditingReplyId(null);
        setEditingReplyText('');
        setConfirmDeleteReplyIndex(null);
    };

    const handleSaveReply = async () => {
        if (!feedbackDoc || editingReplyIndex === null || editingReplyIndex === undefined)
            return;
        const text = editingReplyText.trim();
        if (!text) {
            message.error('Reply cannot be empty.');
            return;
        }
        setIsLoading(true);
        try {
            let response;
            if (editingReplyId) {
                response = await instance.patch(
                    `/student-question-feeback/${feedbackDoc._id}/replies/${editingReplyId}`,
                    { text }
                );
            } else {
                const updatedReplies = replies.map((reply, index) =>
                    index === editingReplyIndex ? { ...reply, text } : reply
                );
                response = await instance.patch(
                    `/student-question-feeback/${feedbackDoc._id}`,
                    { replies: updatedReplies }
                );
            }
            const doc = response?.data?.data;
            applyFromDocument(doc);
            setEditingReplyIndex(null);
            setEditingReplyId(null);
            setEditingReplyText('');
        } catch (error) {
            const title =
                error?.response?.data?.errors?.title || 'Unable to update your reply.';
            message.error(title);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteReply = async (replyIndex) => {
        if (!feedbackDoc || replyIndex === null || replyIndex === undefined) return;
        const target = replies[replyIndex];
        if (!target || target.from !== 'student' || target.author_id !== studentId) return;
        if (confirmDeleteReplyIndex !== replyIndex) {
            setConfirmDeleteReplyIndex(replyIndex);
            return;
        }
        setIsLoading(true);
        try {
            let response;
            if (target._id) {
                response = await instance.delete(
                    `/student-question-feeback/${feedbackDoc._id}/replies/${target._id}`
                );
            } else {
                const updatedReplies = replies.filter((_, index) => index !== replyIndex);
                response = await instance.patch(
                    `/student-question-feeback/${feedbackDoc._id}`,
                    { replies: updatedReplies }
                );
            }
            const doc = response?.data?.data;
            applyFromDocument(doc);
        } catch (error) {
            const title =
                error?.response?.data?.errors?.title || 'Unable to delete your reply.';
            message.error(title);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            setHasNewReply(false);
        }
    }, [isOpen]);

    const renderInitialForm = () => (
        <div className="feedback_initial">
            <h3 className="feedback_title">Ask or give feedback about this question</h3>
            {!canAddNewComment && (
                <div className="feedback_limit_message" style={{ color: '#ff4d4f', marginBottom: '10px', fontSize: '14px' }}>
                    You have reached the maximum limit of 2 comments.
                </div>
            )}
            <textarea
                className="feedback_textarea"
                rows={4}
                placeholder="Write your confusion, error in options, or any feedback..."
                value={feedbackText}
                onChange={(event) => setFeedbackText(event.target.value)}
                disabled={!canAddNewComment}
            />
            <button
                type="button"
                className="feedback_primary_btn"
                onClick={handleCreateFeedback}
                disabled={isLoading || !canAddNewComment}
            >
                Send feedback
            </button>
        </div>
    );

    const renderFeedbackPost = () => {
        if (!feedbackDoc) return null;
        const isOwner = feedbackDoc.student_id === studentId;
        return (
            <div className="feedback_post">
                <div className="feedback_post_header">
                    <div className={'feedback_post_meta  ' + (isOwner ? 'feedback_post_meta_student' : '')}>
                        <span className="feedback_author_chip">
                            {isOwner ? 'You' : 'Student'}
                        </span>
                    </div>
                    {isOwner && !hasAuthorReply && (
                        <div className="feedback_post_actions">
                            {!editingFeedback && !confirmDeleteFeedback && (
                                <>
                                    <button
                                        type="button"
                                        className="feedback_link_btn"
                                        onClick={handleStartEditFeedback}
                                    >
                                        Edit
                                    </button>
                                    <button
                                        type="button"
                                        className="feedback_link_btn danger"
                                        onClick={handleDeleteFeedback}
                                    >
                                        Delete
                                    </button>
                                </>
                            )}
                            {confirmDeleteFeedback && (
                                <div className="feedback_inline_confirm">
                                    <span className="feedback_confirm_text">
                                        Delete feedback?
                                    </span>
                                    <button
                                        type="button"
                                        className="feedback_link_btn danger"
                                        onClick={handleDeleteFeedback}
                                    >
                                        Yes
                                    </button>
                                    <button
                                        type="button"
                                        className="feedback_link_btn"
                                        onClick={() => setConfirmDeleteFeedback(false)}
                                    >
                                        No
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {!editingFeedback && (
                    <p className="feedback_post_text">{feedbackDoc.feedback}</p>
                )}

                {editingFeedback && (
                    <div className="feedback_edit_block">
                        <textarea
                            className="feedback_textarea"
                            rows={3}
                            value={feedbackText}
                            onChange={(event) => setFeedbackText(event.target.value)}
                        />
                        <div className="feedback_edit_actions">
                            <button
                                type="button"
                                className="feedback_primary_btn small"
                                onClick={handleSaveFeedback}
                                disabled={isLoading}
                            >
                                Save
                            </button>
                            <button
                                type="button"
                                className="feedback_link_btn"
                                onClick={handleCancelEditFeedback}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderReplies = () => {
        if (!feedbackDoc) return null;
        const firstAuthorIndex = replies.findIndex((reply) => reply.from === 'author');
        return (
            <div className="feedback_replies">
                {replies.map((reply, index) => {
                    const isStudentReply = reply.from === 'student';
                    const isOwnReply =
                        isStudentReply && reply.author_id && reply.author_id === studentId;
                    const canEditOwnReply =
                        isOwnReply &&
                        (firstAuthorIndex === -1 || index > firstAuthorIndex);
                    const isEditing = editingReplyIndex === index;
                    return (
                        <div
                            key={reply._id || `${reply.from}-${reply.createdAt}-${reply.text}`}
                            className={`feedback_reply ${
                                reply.from === 'author'
                                    ? 'feedback_reply_author'
                                    : 'feedback_reply_student'
                            }`}
                        >
                            <div className="feedback_reply_body">
                                <div className="feedback_reply_meta">
                                    <span className="feedback_reply_from">
                                        {reply.from === 'author' ? 'Author' : 'You'}
                                    </span>
                                </div>
                                {!isEditing && (
                                    <p className="feedback_reply_text">{reply.text}</p>
                                )}
                                {isEditing && (
                                    <div className="feedback_reply_edit">
                                        <textarea
                                            className="feedback_textarea"
                                            rows={2}
                                            value={editingReplyText}
                                            onChange={(event) =>
                                                setEditingReplyText(event.target.value)
                                            }
                                        />
                                        <div className="feedback_edit_actions">
                                            <button
                                                type="button"
                                                className="feedback_primary_btn small"
                                                onClick={handleSaveReply}
                                                disabled={isLoading}
                                            >
                                                Save
                                            </button>
                                            <button
                                                type="button"
                                                className="feedback_link_btn"
                                                onClick={handleCancelEditReply}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {canEditOwnReply && !isEditing && (
                                <div className="feedback_reply_actions">
                                    {confirmDeleteReplyIndex !== index && (
                                        <>
                                            <button
                                                type="button"
                                                className="feedback_link_btn"
                                                onClick={() => handleStartEditReply(index, reply)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="feedback_link_btn danger"
                                                onClick={() => handleDeleteReply(index)}
                                            >
                                                Delete
                                            </button>
                                        </>
                                    )}
                                    {confirmDeleteReplyIndex === index && (
                                        <div className="feedback_inline_confirm reply">
                                            <span className="feedback_confirm_text">
                                                Delete reply?
                                            </span>
                                            <button
                                                type="button"
                                                className="feedback_link_btn danger"
                                                onClick={() => handleDeleteReply(index)}
                                            >
                                                Yes
                                            </button>
                                            <button
                                                type="button"
                                                className="feedback_link_btn"
                                                onClick={() => setConfirmDeleteReplyIndex(null)}
                                            >
                                                No
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderReplyBox = () => {
        if (!feedbackDoc || !hasAuthorReply) return null;
        return (
            <div className="feedback_reply_box">
                {!canAddNewComment && (
                    <div className="feedback_limit_message" style={{ color: '#ff4d4f', marginBottom: '10px', fontSize: '14px' }}>
                        You have reached the maximum limit of 2 comments.
                    </div>
                )}
                <textarea
                    className="feedback_textarea"
                    rows={2}
                    placeholder="Write a reply..."
                    value={replyText}
                    onChange={(event) => setReplyText(event.target.value)}
                    disabled={!canAddNewComment}
                />
                <button
                    type="button"
                    className="feedback_primary_btn small"
                    onClick={handleSubmitReply}
                    disabled={isLoading || !canAddNewComment}
                >
                    Submit reply
                </button>
            </div>
        );
    };

    return (
        <div className="question-feedback-wrapper">
            <span
                className={`question-action-icon question-feedback-icon ${
                    hasNewReply ? 'question-feedback-icon--new' : ''
                }`}
                title="Comment on question"
                onClick={openOrToggle}
                role="button"
                tabIndex={0}
                onKeyPress={(event) => {
                    if (event.key === 'Enter') openOrToggle();
                }}
            >
                <CommentOutlined />
            </span>

            {isOpen && (
                <div
                    className="question_feedback_backdrop"
                    onClick={closeModal}
                    role="presentation"
                >
                    <div
                        className="question_feedback_content"
                        role="dialog"
                        aria-modal="true"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="feedback_close_btn"
                            onClick={closeModal}
                            aria-label="Close"
                        >
                            <CloseOutlined />
                        </button>

                        {isLoading && (
                            <div className="feedback_loading">
                                <Spin size="large" />
                            </div>
                        )}

                        {!isLoading && !feedbackDoc && renderInitialForm()}

                        {!isLoading && feedbackDoc && (
                            <>
                                {renderFeedbackPost()}
                                {renderReplies()}
                                {renderReplyBox()}
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default QuestionFeedback;

